import { Alert, AlertProps, AlertTitle, Button, Stack } from '@mui/material'
import { Device } from '../types'
import { RuuviTagType } from '../enums'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { updateDevice } from '../services/deviceService'
import { updateValve } from '../services/valve'
import { LoadingButton } from '@mui/lab'
import { formatMacAddress } from '../utils/string'
import { useDispatch } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'

export interface DeviceDetachRuuviTagAlertProps extends Omit<AlertProps, 'severity' | 'children'> {
  device: Device
  type: RuuviTagType.ATTACHED | RuuviTagType.VALVE_IN | RuuviTagType.VALVE_OUT
  onCancel?: () => void
  onUpdate?: (device: Device) => void
}

export default function DeviceDetachRuuviTagAlert({
  device,
  type,
  onCancel,
  onUpdate,
  ...props
}: DeviceDetachRuuviTagAlertProps) {
  const { t } = useTranslation()

  const dispatch = useDispatch()

  const [isSubmitting, setSubmitting] = useState(false)

  async function handleSubmit() {
    try {
      setSubmitting(true)

      const controller = new AbortController()

      const updatedDevice = await detachRuuviTag(device.name, controller)

      dispatch(createSuccessNotification(t('installation:detachRuuviTagSuccess')))

      onUpdate?.(updatedDevice)
    } catch {
      dispatch(createErrorNotification(t('installation:detachRuuviTagError')))
    } finally {
      setSubmitting(false)
    }
  }

  async function detachRuuviTag(name: string, controller: AbortController) {
    switch (type) {
      case RuuviTagType.ATTACHED:
        return await updateDevice(name, { attachedRuuviTag: null }, { signal: controller.signal })

      case RuuviTagType.VALVE_IN:
        return await updateValve(name, { inRuuviTag: null }, { signal: controller.signal })

      case RuuviTagType.VALVE_OUT:
        return await updateValve(name, { outRuuviTag: null }, { signal: controller.signal })
    }
  }

  // Check if there is anthing to detach.
  if (
    (type === RuuviTagType.ATTACHED && device.attachedRuuviTagId === null) ||
    (type === RuuviTagType.VALVE_IN && device.inRuuviTagId === null) ||
    (type === RuuviTagType.VALVE_OUT && device.outRuuviTagId === null)
  ) {
    return null
  }

  return (
    <Alert severity="warning" {...props}>
      <AlertTitle sx={{ mt: 0 }}>
        {type === RuuviTagType.ATTACHED && t('ruuvitag:confirmDetachRuuviTagFromDevice')}

        {type === RuuviTagType.VALVE_IN &&
          device.inRuuviTagId !== null &&
          t('installation:detachRuuviTagWarning', {
            ruuviTag: formatMacAddress(device.inRuuviTagId)
          })}

        {type === RuuviTagType.VALVE_OUT &&
          device.outRuuviTagId !== null &&
          t('installation:detachRuuviTagWarning', {
            ruuviTag: formatMacAddress(device.outRuuviTagId)
          })}
      </AlertTitle>

      <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 2 }}>
        <Button variant="outlined" color="inherit" disabled={isSubmitting} onClick={onCancel}>
          {t('actions:cancel')}
        </Button>

        <LoadingButton
          variant="contained"
          color="warning"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('actions:confirm')}
        </LoadingButton>
      </Stack>
    </Alert>
  )
}
